/**
 * Payroll shared helpers — split out of pages/Payroll.jsx (2026-02).
 *
 * Exports the palette + formatters + sort/date helpers used by every
 * payroll subcomponent. Zero behaviour change from the pre-split
 * inline definitions.
 */

// ── palette (aligned with the rest of the app)
const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', teal: '#3B6F5D', rose: '#8A3A3A', amber: '#B08A3A',
  ok: '#3B6F5D',
};

const usd = (cents) => `$${((Number(cents) || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usdShort = (cents) => `$${((Number(cents) || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const pct = (n, digits = 1) => `${(Number(n) * 100).toFixed(digits)}%`;
const int = (n) => (Number(n) || 0).toLocaleString();

// Bucket a therapist's category_snapshot into "Employee" vs "Contractor".
// Employees come first alphabetically, then contractors alphabetically.
const CATEGORY_GROUP = (cat) => {
  const c = String(cat || '').toLowerCase();
  if (c.startsWith('contractor')) return 'Contractor';
  return 'Employee';
};
const CATEGORY_GROUP_ORDER = { Employee: 0, Contractor: 1 };
const _lastName = (name) => {
  // Strip anything after the first comma (usually credentials like
  // ", M.S., CCC-SLP" or ", PhD"), then filter out known credential
  // tokens from the remaining whitespace-separated parts so the true
  // surname wins the sort — e.g. "Brandi Melchor M.S. CCC-SLP" → "melchor".
  const CRED = /^(m\.?s\.?|m\.?a\.?|m\.?ed\.?|ph\.?d\.?|ed\.?d\.?|slp|slpa|otr|otr\/l|ot|pt|dpt|cf-?slp|ccc-?slp|ccc-?a|ba|bs|ma|md|do|jr\.?|sr\.?|ii|iii|iv)$/i;
  const bare = String(name || '').split(',')[0].trim();
  const parts = bare.split(/\s+/).filter(p => p && !CRED.test(p));
  return (parts[parts.length - 1] || bare || '').toLowerCase();
};
// Return rows sorted by (group, last name asc, full name asc). Uses
// localeCompare for case-/accent-insensitive natural ordering so therapist
// names sort correctly regardless of credential suffixes or accents.
function sortAndGroupRows(rows) {
  return [...rows].sort((a, b) => {
    const ga = CATEGORY_GROUP_ORDER[CATEGORY_GROUP(a.category_snapshot)];
    const gb = CATEGORY_GROUP_ORDER[CATEGORY_GROUP(b.category_snapshot)];
    if (ga !== gb) return ga - gb;
    const la = _lastName(a.therapist_name_snapshot);
    const lb = _lastName(b.therapist_name_snapshot);
    const cmp = la.localeCompare(lb, 'en', { sensitivity: 'base' });
    if (cmp !== 0) return cmp;
    return String(a.therapist_name_snapshot || '').localeCompare(
      String(b.therapist_name_snapshot || ''), 'en', { sensitivity: 'base' });
  });
}

// Same shape as sortAndGroupRows but keyed on the fields the Therapist
// Setup Dialog roster uses (`category`, `attendance_name`) instead of
// the run-row snapshot fields — so employees sort alphabetically first
// and contractors sort alphabetically underneath them.
function sortTherapistsForSetup(rows) {
  return [...rows].sort((a, b) => {
    const ga = CATEGORY_GROUP_ORDER[CATEGORY_GROUP(a.category)];
    const gb = CATEGORY_GROUP_ORDER[CATEGORY_GROUP(b.category)];
    if (ga !== gb) return ga - gb;
    const la = _lastName(a.attendance_name);
    const lb = _lastName(b.attendance_name);
    if (la !== lb) return la < lb ? -1 : 1;
    return String(a.attendance_name || '').localeCompare(b.attendance_name || '');
  });
}

// Thursday → Wednesday helper. Uses America/Chicago wall clock so the
// suggested pay period stays honest even when the browser opens the page
// after 7pm CT (when UTC has already ticked to the next calendar day).
function _ctDate() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short'
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  const wk = parts.find(p => p.type === 'weekday').value; // Sun, Mon, Tue…
  const dowMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  // Anchor at local noon so subsequent setDate() arithmetic doesn't cross a DST edge.
  const d0 = new Date(`${y}-${m}-${d}T12:00:00`);
  return { d0, dow: dowMap[wk] };
}
function _isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function biweeklyDefaults() {
  // Find the most recent Wednesday (CT), then span 14 days back to Thursday.
  const { d0: today, dow } = _ctDate(); // dow: 0 Sun … 3 Wed
  const daysBackToWed = (dow - 3 + 7) % 7;
  const end = new Date(today); end.setDate(today.getDate() - daysBackToWed);
  const start = new Date(end); start.setDate(end.getDate() - 13);
  return { period_start: _isoLocal(start), period_end: _isoLocal(end) };
}

export {
  C, usd, usdShort, pct, int,
  CATEGORY_GROUP, CATEGORY_GROUP_ORDER, _lastName,
  sortAndGroupRows, sortTherapistsForSetup,
  _ctDate, _isoLocal, biweeklyDefaults,
};
